import { useEffect, useState } from "react";
import { create } from "zustand";

/**
 * LiveRegion — single off-screen ARIA live region rendered once at the
 * root. Anywhere in the app can call `announce("…")` to push a message
 * the screen reader will read out. Two channels:
 *
 *   "polite"  → normal status updates (page changed, item saved)
 *   "assertive" → critical alerts (gas alarm fired)
 *
 * The DOM nodes are visually hidden but discoverable to AT software.
 * We keep the previous message visible for 200 ms before clearing so
 * back-to-back identical strings still get read out (otherwise the AT
 * skips them as no-change).
 */

type Channel = "polite" | "assertive";

interface LiveState {
  polite: string;
  assertive: string;
  push: (channel: Channel, message: string) => void;
}

const useLive = create<LiveState>()((set) => ({
  polite: "",
  assertive: "",
  push: (channel, message) =>
    set((state) => ({ ...state, [channel]: makeUnique(state[channel], message) })),
}));

function makeUnique(prev: string, next: string): string {
  if (prev === next) return next + "​";          // append zero-width space
  return next;
}

export function announce(message: string, channel: Channel = "polite"): void {
  useLive.getState().push(channel, message);
}

export function LiveRegion() {
  const polite = useLive((s) => s.polite);
  const assertive = useLive((s) => s.assertive);

  // Clear polite messages 1.5 s after they land so the next one with
  // the same text still triggers a read-out.
  const [politeShown, setPoliteShown]       = useState(polite);
  const [assertiveShown, setAssertiveShown] = useState(assertive);
  useEffect(() => { setPoliteShown(polite); const t = setTimeout(() => setPoliteShown(""), 1500); return () => clearTimeout(t); }, [polite]);
  useEffect(() => { setAssertiveShown(assertive); const t = setTimeout(() => setAssertiveShown(""), 2000); return () => clearTimeout(t); }, [assertive]);

  return (
    <>
      <div role="status" aria-live="polite"    aria-atomic="true" style={SR_ONLY}>{politeShown}</div>
      <div role="alert"  aria-live="assertive" aria-atomic="true" style={SR_ONLY}>{assertiveShown}</div>
    </>
  );
}

const SR_ONLY: React.CSSProperties = {
  position: "absolute",
  width: 1,
  height: 1,
  padding: 0,
  margin: -1,
  overflow: "hidden",
  clip: "rect(0 0 0 0)",
  whiteSpace: "nowrap",
  border: 0,
};
